import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, X, Loader2, CalendarDays } from "lucide-react";
import { api } from "../lib/api";
import { EASE } from "../components/motion";
import { useLang } from "../lib/i18n";

export default function ConfirmAppointment() {
  const { token } = useParams();
  const { t } = useLang();
  const [appt, setAppt] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get(`/appointments/${token}`)
      .then((r) => setAppt(r.data))
      .catch(() => setError(t("confirm.notFound")));
  }, [token]);

  const decide = async (status) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/appointments/${token}/status`, { status });
      setAppt(data);
    } finally {
      setBusy(false);
    }
  };

  const statusLabel = appt ? t(`confirm.${appt.status}`) : "";

  return (
    <div className="min-h-screen bg-cream grain-overlay flex items-center justify-center px-6 py-16" data-testid="confirm-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: EASE }}
        className="w-full max-w-lg bg-white border border-taupe/20 rounded-3xl p-10 shadow-[0_8px_30px_rgba(44,36,27,0.06)]"
      >
        <img src="/logo.png" alt="Nadou Skin" className="h-12 w-auto" />
        {error ? (
          <p className="mt-8 text-sm text-[#9E4747]" data-testid="confirm-error">{error}</p>
        ) : !appt ? (
          <div className="mt-8 flex items-center gap-3 text-sm text-espresso/55">
            <Loader2 className="w-4 h-4 animate-spin" /> {t("confirm.loading")}
          </div>
        ) : (
          <div className="mt-8" data-testid="confirm-details">
            <div className="flex items-center gap-3 text-taupe-dark">
              <CalendarDays className="w-5 h-5" strokeWidth={1.5} />
              <span className="text-[12px] tracking-[0.2em] uppercase">{t("confirm.kicker")}</span>
            </div>
            <h1 className="font-display text-3xl text-espresso mt-4">{appt.service_name}</h1>
            <dl className="mt-6 space-y-3 text-sm">
              <div className="flex justify-between border-b border-taupe/15 pb-3">
                <dt className="text-espresso/55">{t("confirm.date")}</dt>
                <dd className="text-espresso font-medium" data-testid="confirm-date">{appt.date} · {appt.time}</dd>
              </div>
              <div className="flex justify-between border-b border-taupe/15 pb-3">
                <dt className="text-espresso/55">{t("confirm.client")}</dt>
                <dd className="text-espresso font-medium" data-testid="confirm-client">{appt.name}</dd>
              </div>
              <div className="flex justify-between border-b border-taupe/15 pb-3">
                <dt className="text-espresso/55">{t("confirm.phone")}</dt>
                <dd className="text-espresso font-medium">{appt.phone}</dd>
              </div>
              <div className="flex justify-between border-b border-taupe/15 pb-3">
                <dt className="text-espresso/55">{t("confirm.price")}</dt>
                <dd className="text-espresso font-medium">{appt.price_label}</dd>
              </div>
              {appt.message && (
                <div className="pt-1">
                  <dt className="text-espresso/55">{t("confirm.message")}</dt>
                  <dd className="text-espresso mt-1 font-light">{appt.message}</dd>
                </div>
              )}
            </dl>
            <p
              className={`mt-6 text-sm font-medium ${
                appt.status === "confirmed" ? "text-[#4A6B53]" : appt.status === "refused" ? "text-[#9E4747]" : "text-taupe-dark"
              }`}
              data-testid="confirm-status"
            >
              {statusLabel}
            </p>
            {appt.status === "pending" && (
              <div className="mt-6 flex gap-3">
                <button
                  data-testid="confirm-accept-btn"
                  disabled={busy}
                  onClick={() => decide("confirmed")}
                  className="flex-1 rounded-full bg-[#4A6B53] text-white px-6 py-4 text-sm tracking-[0.1em] uppercase hover:brightness-110 transition-[filter] duration-300 inline-flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Check className="w-4 h-4" strokeWidth={1.5} /> {t("confirm.confirmBtn")}
                </button>
                <button
                  data-testid="confirm-refuse-btn"
                  disabled={busy}
                  onClick={() => decide("refused")}
                  className="flex-1 rounded-full border border-[#9E4747]/40 text-[#9E4747] px-6 py-4 text-sm tracking-[0.1em] uppercase hover:bg-[#9E4747]/5 transition-colors duration-300 inline-flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <X className="w-4 h-4" strokeWidth={1.5} /> {t("confirm.refuseBtn")}
                </button>
              </div>
            )}
          </div>
        )}
        <Link to="/" className="block mt-8 text-xs tracking-[0.14em] uppercase text-espresso/45 hover:text-espresso transition-colors duration-300">
          {t("confirm.back")}
        </Link>
      </motion.div>
    </div>
  );
}
